/*************************************************************************\
Provides a client side interface for interacting with CommonAPI & AdminAPI
and indirectly it will automatically interact with AuthAPI if necessary.

General methods available:

    api.get(route, includeAuth:false)
    api.post(route, data:null)
    api.put(route, data:null)
    api.del(route, data:null)

    api.admin.get(route) // always includes auth
    api.admin.post(route, data:null)
    api.admin.put(route, data:null)
    api.admin.del(route, data:null)

The "route" argument can be a simple string route (relative to the API
base path provided in config) - or it can be an array of strings/objects.
If it is an array then strings used as path segments and are joined
together (separated by slashes), whereas objects are used as bags of
query-string key/value pairs. Eg:

    // "the/api/route?a=1&foo=bar"
    api.post(['the', 'api', 'route', { a: 1, foo: 'bar' }], data)
       .then(...);

Getting the returned data from the above calls can be done using the
returned promise containing the response data. Eg:

    api.get('the/api/route')
        .then(function({ ok, status, data }) {
            if (ok) doSomethingWith(data);
            else if (status === 400) dealWithBadRequestResponse(data);
            // else other status codes...
        })

Optionally you may catch exceptions (which can include auth failures),
but uncaught exceptions (including auth exceptions) will be handled
by a global app-scope exception handler; this allows for the app to
automatically handle general exceptions that specific code would either
not know how to deal with or would have to repeat handler code everywhere.

        .catch(function(ex) {
            if (ex instanceof api.auth.exceptions.UnauthorizedException)
                console.log('Not signed-in.');
            else if (ex instanceof api.auth.exceptions.ForbiddenException)
                console.log('Permission denied.');
            else
                console.log('Some unexpected exception: ' + ex);
        });

Note that 'get' requests cannot send data (payload) apart from url params.
Note that 'post','put', and 'del' requests must always be authenticated.

Normal exceptions that may be thrown by the API auth checking system include:
    - api.exceptions.UnauthorizedException
        Indicating the user is not signed in, or their sign-in session is invalid.
        Anonymous API calls would never get this.
    - api.exceptions.ForbiddenException
        Indicating the user is denied permission for the specific request.
        This is used for admin users (I don't think we would send this for bidder calls).

See export at bottom of file for all public properties.
\*************************************************************************/

/* eslint no-console: off */

import { config } from '../app/config.js';
import { log } from '../app/log.js';
import { auth } from './auth.js';
import { url as urlTool } from '../utility/tools/url.js';

/* Private Classes *******************************************************/

class FetchOptions {
    constructor(options) {
        // Defaults.
        this.method = 'get';
        this.mode = 'cors';
        this.cache = 'no-store';

        // Apply custom values (possibly adding values and/or overiding defaults).
        Object.assign(this, options);

        // If headers hasn't been provided then create the default headers set,
        // or if headers isn't of the correct type then create a new Headers object
        // and init with the provided headers data (assume it is valid).
        if (!this.headers) {
            // Add correct content-type header for api calls.
            // We used to specify 'text/plain' so as to avoid CORS pre-flight requests (OPTIONS),
            // however dotnet 3.1 introduced System.Text.Json which didn't support lots of stuff
            // so we had to use their fallback AddNewtonsoftJson feature, which in turn screwed
            // up our ability to accept 'text/plain' content-types... so now we're just using the
            // more normal 'application/json' content-type (which is maybe better?).
            this.headers = new Headers({ 'content-type': 'application/json' });
        }
        else if (!(this.headers instanceof Headers)) {
            this.headers = new Headers(this.headers || {});
        }

        // Options indicate we should include the Authorization header (default to not include).
        if (this.includeAuth) {
            auth.addAuthorizationHeader(this.headers);
        }
    }
}

/* Private Functions *****************************************************/

function resultPromise(response) {
    return response.text().then(data => {
        if (data && data.length) {
            const contentType = response.headers.get('content-type'),
                  contentDisposition = response.headers.get('content-disposition');

            if (contentType === 'application/octet-stream' || (contentDisposition && contentDisposition.indexOf('attachement') >= 0)) {
                // File, just leave as raw string.
            }
            else if (data[0] === '{' && data[data.length - 1] === '}') {
                // Looks like JSON so try to parse.
                // Note that we dont accept root level arrays [...] as JSON due to a potential vunerability in allowing raw Arrays as JSON.
                try {
                    // Decode into json (we generally expect all/most result data to be json, apart from errors sometimes).
                    data = JSON.parse(data);
                }
                catch (exJson) {
                    log.critical('api.resultPromise{catch-json}', `${response.status}: ${exJson.message}`);
                }
            }
            // No match so just leave as raw string.
        }

        // Return a resolved promise including the output (either parsed JSON or just a text string).
        return Promise.resolve({
            ok: response.ok,
            status: response.status,
            data: data
        });
    })
    .catch(exText => {
        log.critical('api.resultPromise{catch-text}', `${response.status}: ${exText.message}`);

        // Return response with no data since decoding json and text failed.
        return Promise.resolve({
            ok: response.ok,
            status: response.status,
            data: null
        });
    });
}

function request(endpoint, method, route, data, options, includeAuth, allowRefresh) {
    const dataInvalid = data === null || typeof data === 'undefined' || (data.hasOwnProperty && data.hasOwnProperty('length') && !data.length);

    // Check/serialize outgoing data.
    if (method === 'get' || dataInvalid) {
        data = void 0; // undefined
    }
    else if (data instanceof FormData) {
        // No need to do anything to data, it is already good.

        // FetchOptions will default the content-type to 'text/plain' which is good for the normal case were we
        // are sending JSON data, however for FormData this will not work. FormData needs the content-type to be
        // 'multipart/form-data' + boundary identifier, this will be automatically added by the browser if we don't
        // specify a content-type ourselves.. so therefore we need to provide a headers object to FetchOptions so
        // that it does NOT add the default content-type.
        options = options || {};
        options.headers = options.headers || {};
    }
    else if (typeof data === 'object') {
        data = JSON.stringify(data);
    }

    return fetch(
        endpoint + route,
        new FetchOptions({
            includeAuth: includeAuth,
            method: method,
            body: data,
            headers: options && options.headers ? options.headers : void 0
        }))
        .then(function (response) {
            if (response.status === 403) {
                // 403: Forbidden
                throw new auth.exceptions.ForbiddenException(`api:request:${method}:${response.url}`);
            }
            else if (response.status === 401) {
                // 401: Unauthorized (either invalid/expired token, or token needs to be refreshed).

                // If we allow auto refresh AND we're including auth then we try to get a refreshed token
                // and if we succeed then automatically retry the original request.
                if (allowRefresh && includeAuth) {
                    // Try to get a fresh token from the auth API.
                    return auth.refresh().then(result => {
                        // Successfully refreshed token.
                        if (result.ok) {
                            // Retry the original request.
                            // NOTE: The new token is automatically included in the request (see FetchOptions).
                            // IMPORTANT: allowRefresh = false so we don't have infinite recursion.
                            return request(endpoint, method, route, data, options, includeAuth, false);
                        }
                        else throw new auth.exceptions.UnauthorizedException(`api:request{refreshed}:${method}:${response.url}`);
                    })
                    .catch((ex) => {
                        log.critical(`api.${method}:request{refreshed}`, ex);
                        throw ex;
                    });
                }
                else throw new auth.exceptions.UnauthorizedException(`api:request:${method}:${response.url}`);
            }
            else { // Otherwise nothing else to do here, just return result.
                return resultPromise(response);
            }
        })
        .catch((ex) => {
            log.critical(`api.${method}:request{catch}`, ex);
            throw ex;
        });
}

function postLog(data) {
    if (typeof data === 'object') {
        data = JSON.stringify(data);
    }

    return fetch(
        `${config.endpoints.commonAPI}logging`,
        new FetchOptions({
            includeAuth: true,
            method: 'post',
            body: data
        }))
        .then(function (response) {
            if (response.status === 403) {
                // 403: Forbidden
                // Don't log or throw exception to avoid inifinite logging recursion.
                console.log('api:log{forbidden}');
            }
            else if (response.status === 401) {
                // 401: Unauthorized (either invalid/expired token, or token needs to be refreshed).
                // Don't log or throw exception to avoid inifinite logging recursion.
                console.log('api:log{unauthorized}');
            }
            // Otherwise nothing else to do here, just return without doing anything (there shouldn't be any results data).
        })
        .catch((ex) => {
            // Don't log or throw exception to avoid inifinite logging recursion.
            console.log(`api:log{catch}: ${ex.message}`);
        });
}

/* Public Interface ******************************************************/

/**
 * Call the CommonAPI using GET method.
 * GET calls should be used to retrieve records, not modify them.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {Boolean} [includeAuth] GET requests don't include the user auth session token by default, if needed specify true here.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _get(route, includeAuth, options) {
    return request(config.endpoints.commonAPI, 'get', urlTool.assemble(route), void 0, options, includeAuth === true, true);
}
/**
 * Call the CommonAPI using POST method.
 * POST calls should be used to create new records, not update records (nor create/update), nor delete or only-retrieve records.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {(string|object|FormData)} data The data (body) you want to send in the POST.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _post(route, data, options) {
    return request(config.endpoints.commonAPI, 'post', urlTool.assemble(route), data, options, true, true);
}
/**
 * Call the CommonAPI using PUT method.
 * PUT calls should be used to update records (or create/update), not when a new record is always created, nor delete or only-retrieve records.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {(string|object|FormData)} data The data (body) you want to send in the PUT.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _put(route, data, options) {
    return request(config.endpoints.commonAPI, 'put', urlTool.assemble(route), data, options, true, true);
}
/**
 * Call the CommonAPI using DELETE method.
 * DELETE calls should be used to delete records, not create or update or retrieve records.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {(string|object|FormData)} data The data (body) you want to send in the DELETE.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _del(route, data, options) {
    return request(config.endpoints.commonAPI, 'delete', urlTool.assemble(route), data, options, true, true);
}

/**
 * Call the AdminAPI using GET method.
 * GET calls should be used to retrieve records, not modify them.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {Boolean} [includeAuth] GET requests don't include the user auth session token by default, if needed specify true here.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _getAdmin(route, options) {
    return request(config.endpoints.adminAPI, 'get', urlTool.assemble(route), void 0, options, true, true);
}
/**
 * Call the AdminAPI using POST method.
 * POST calls should be used to create new records, not update records (nor create/update), nor delete or only-retrieve records.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {(string|object|FormData)} data The data (body) you want to send in the POST.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _postAdmin(route, data, options) {
    return request(config.endpoints.adminAPI, 'post', urlTool.assemble(route), data, options, true, true);
}
/**
 * Call the AdminAPI using PUT method.
 * PUT calls should be used to update records (or create/update), not when a new record is always created, nor delete or only-retrieve records.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {(string|object|FormData)} data The data (body) you want to send in the PUT.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _putAdmin(route, data, options) {
    return request(config.endpoints.adminAPI, 'put', urlTool.assemble(route), data, options, true, true);
}
/**
 * Call the AdminAPI using DELETE method.
 * DELETE calls should be used to delete records, not create or update or retrieve records.
 * 
 * @param {!string|Array} route The API URL route/path/values?query to fetch.
 * @param {(string|object|FormData)} data The data (body) you want to send in the DELETE.
 * @param {Object} [options] Optional request options, eg headers.
 * @returns {Promise} A promise that will return once the fetch has completed, the result argument has: { ok:boolean, status:string, data:(object|string) }
 */
function _delAdmin(route, data, options) {
    return request(config.endpoints.adminAPI, 'delete', urlTool.assemble(route), data, options, true, true);
}

export const api = {
    auth: auth,

    // This api.log call should only be used from within log,
    // special functionality that is necessary(rather than just using post) so as to avoid infinite recursion.
    log: (data) => postLog(data),

    get: _get,
    post: _post,
    put: _put,
    del: _del,

    admin: {
        get: _getAdmin,
        post: _postAdmin,
        put: _putAdmin,
        del: _delAdmin
    }
};